import { Button, Col, Container, Form, Row } from "react-bootstrap"
import { navigateTo } from "../../redux/router/routerActions"
import { useAppDispatch } from "../../redux/store"
import QuickForm from "../../Components/Forms/QuickForm/QuickForm"
import { convertActiveQueryToUrl } from "../../Helpers/Query/queryHelpers";
import useActiveQueryHook from "../../Hooks/UseActiveQuery/useActiveQueryHook";
import { useBulkEditAdditionalServicesMutation, useGetAdditionalServicesQuery } from "../../services/bClientApi"
import MongoPagination from "../../Components/Pagination/MongoPagination"
import { AdditionalServiceType } from "../../Types/additionalServiceType"
import AdditionalService from "../../Components/AdditionalService/AdditionalServiceEntry/AdditionalServiceEntry"
import { useState } from "react"
import { ConfirmModal } from "../../Components/Modals/confirmModal"
import useMyProperties from "../../Hooks/UseMyProperties/UseMyPropertiesHook"
import useMyPropertiesSelectValues from "../../Hooks/UseMyProperties/UseMyPropertySelectValuesHook"
import useUserRefs from "../../Hooks/UseUsersQuery/UseUserRefs"
import useUsersSelectValues from "../../Hooks/UseUsersQuery/UseUsersSelectValues"

const statusOptions = [
    { value: 'request', text: 'Request' },
    { value: 'new', text: 'New' },
    { value: 'pending', text: 'Pending' },
    { value: 'started', text: 'Started' },
    { value: 'completed', text: 'Completed' },
    { value: 'ready', text: 'Ready' },
    { value: 'canceled', text: 'Canceled' },
]

export default function AdditionalServicesPage() {

    const dispatch = useAppDispatch()

    const { myProperties } = useMyProperties()
    const propertyOptions = useMyPropertiesSelectValues(myProperties)
    const userRefs = useUserRefs()
    const userOptions = useUsersSelectValues(userRefs, { valueKey: '_id' })

    const [disableButtons, setDisableButtons] = useState(false)
    const [selectedServices, setSelectedServices] = useState<{ [key: string]: boolean }>({})
    const [bulkStatusValue, setBulkStatusValue] = useState('')
    const [activeConfirmationModal, setActiveConfirmationModal] = useState({
        active: false,
        title: '',
        message: '',
        onConfirm: () => { },
        onClose: () => { },
    })

    const resetConfirmationModal = () => {
        setActiveConfirmationModal({
            active: false,
            title: '',
            message: '',
            onConfirm: () => { },
            onClose: () => { },
        })
        setDisableButtons(false)
    }

    const [
        bulkEditAdditionalServices,
        {
            isLoading: isEditing,
        }
    ] = useBulkEditAdditionalServicesMutation()

    const { activeQuery } = useActiveQueryHook({
        query: {},
        limit: 10,
    })

    const {
        data,
        error,
        isLoading,
    } = useGetAdditionalServicesQuery({
        additionalServicesQuery: activeQuery.query,
        pagination: {
            page: activeQuery.page,
            limit: activeQuery.limit,
        }
    }, {
        skip: activeConfirmationModal.active || isEditing || disableButtons
    })
    const additionalServicesList = data?.docs || []

    const updateSelectedStatuses = async () => {
        setDisableButtons(true)
        setActiveConfirmationModal(prev => ({
            ...prev,
            message: 'Updating statuses...',
        }))
        const updatedEntries = Object.keys(selectedServices).map(id => ({
            _id: id,
            status: bulkStatusValue,
        }))
        await bulkEditAdditionalServices(updatedEntries).catch(err => {
            console.error('Error updating additional service statuses:', err)
        })
        setSelectedServices({})
        setBulkStatusValue('')
        resetConfirmationModal()
    }

    const loadPage = (pageParams: { query: any, page: number, limit: number }) => {
        const destination = `/additional-services?${convertActiveQueryToUrl(pageParams)}`
        dispatch(navigateTo(destination))
    }

    return (
        <Container>
            <Row>
                <Col>
                    <div className="d-flex justify-content-between align-items-center">
                        <h1>Additional Services</h1>
                        <Button 
                            variant="primary" 
                            onClick={() => dispatch(navigateTo('/add-additional-service'))}
                        >
                            Add New Service
                        </Button>
                    </div>
                    <hr />
                    {error && (<>{JSON.stringify(error)}<br /></>)}

                    <QuickForm
                        layout={{ xs: 2 }}
                        initialData={{
                            serviceDateStart: activeQuery.query.serviceDateStart,
                            serviceDateEnd: activeQuery.query.serviceDateEnd,

                            serviceEndDateStart: activeQuery.query.serviceEndDateStart,
                            serviceEndDateEnd: activeQuery.query.serviceEndDateEnd,

                            createdDateStart: activeQuery.query.createdDateStart,
                            createdDateEnd: activeQuery.query.createdDateEnd,
                            serviceType: activeQuery.query.serviceType,
                            serviceSubType: activeQuery.query.serviceSubType,
                            status: activeQuery.query.status ? activeQuery.query.status.map((s: string) => ({ tag: s })) : [],
                            tempStatus: '',
                            propertyId: activeQuery.query.propertyId,
                            bookingId: activeQuery.query.bookingId,
                            amenityId: activeQuery.query.amenityId,
                            assignedTo: activeQuery.query.assignedTo,
                        }}
                        fields={[
                            {
                                fieldName: 'serviceDateStart',
                                fieldLabel: 'Service Date Start',
                                fieldType: 'date',
                                placeholder: 'Service Date Start',
                                layout: {
                                    xs: 12,
                                    md: 6,
                                }
                            },
                            {
                                fieldName: 'serviceDateEnd',
                                fieldLabel: 'Service Date End',
                                fieldType: 'date',
                                placeholder: 'Service Date End',
                                layout: {
                                    xs: 12,
                                    md: 6,
                                }
                            },

                            {
                                fieldName: 'serviceEndDateStart',
                                fieldLabel: 'Service End Date Start',
                                fieldType: 'date',
                                placeholder: 'Service End Date Start',
                                layout: {
                                    xs: 12,
                                    md: 6,
                                }
                            },

                             {
                                fieldName: 'serviceEndDateEnd',
                                fieldLabel: 'Service End Date End',
                                fieldType: 'date',
                                placeholder: 'Service End Date End',
                                layout: {
                                    xs: 12,
                                    md: 6,
                                }
                            },



                            {
                                fieldName: 'createdDateStart',
                                fieldLabel: 'Created Date Start',
                                fieldType: 'date',
                                placeholder: 'Created Date Start',
                                layout: {
                                    xs: 12,
                                    md: 6,
                                }
                            },
                            {
                                fieldName: 'createdDateEnd',
                                fieldLabel: 'Created Date End',
                                fieldType: 'date',
                                placeholder: 'Created Date End',
                                layout: {
                                    xs: 12,
                                    md: 6,
                                }
                            },
                            {
                                fieldName: 'serviceType',
                                fieldLabel: 'Service Type',
                                fieldType: 'text',
                                placeholder: 'Service Type',
                                layout: {
                                    xs: 12,
                                    md: 6,
                                }
                            },
                            {
                                fieldName: 'serviceSubType',
                                fieldLabel: 'Service Sub Type',
                                fieldType: 'text',
                                placeholder: 'Service Sub Type',
                                layout: {
                                    xs: 12,
                                    md: 6,
                                }
                            },
                            {
                                fieldName: 'propertyId',
                                fieldLabel: 'Property',
                                fieldType: 'select',
                                placeholder: '- Select Property -',
                                options: propertyOptions,
                                layout: {
                                    xs: 12,
                                    md: 6,
                                }
                            },
                            {
                                fieldName: 'bookingId',
                                fieldLabel: 'Booking ID',
                                fieldType: 'text',
                                placeholder: 'Booking ID',
                                layout: {
                                    xs: 12,
                                    md: 6,
                                }
                            },
                            {
                                fieldName: 'amenityId',
                                fieldLabel: 'Amenity ID',
                                fieldType: 'text',
                                placeholder: 'Amenity ID',
                                layout: {
                                    xs: 12,
                                    md: 6,
                                }
                            },
                            {
                                fieldName: 'assignedTo',
                                fieldLabel: 'Assigned To',
                                fieldType: 'select',
                                placeholder: '- Select User -',
                                options: userOptions,
                                layout: {
                                    xs: 12,
                                    md: 6,
                                }
                            },
                            {
                                fieldName: 'status',
                                fieldLabel: 'Status',
                                fieldType: 'quickTag',
                                placeholder: 'Status',
                                extra: {
                                    tempFieldName: 'tempStatus',
                                    tagEntryName: 'tag',
                                    buttons: statusOptions.map(opt => ({ tag: opt.value, text: opt.text })),
                                    options: {
                                        hideInput: true,
                                        hideBadges: true,
                                    },
                                },
                                layout: {
                                    xs: 12,
                                }
                            },
                        ]}
                        disabled={isLoading}
                        onSubmit={(values: any) => {
                            const query: any = { ...values }
                            if (values.status && values.status.length > 0) {
                                query.status = values.status.map((s: any) => s.tag)
                            } else {
                                delete query.status
                            }
                            delete query.tempStatus
                            loadPage({
                                query,
                                page: 1,
                                limit: activeQuery.limit
                            })
                        }}
                    />

                    {additionalServicesList && additionalServicesList.length > 0 && (
                        <Button
                            variant="secondary"
                            size="sm"
                            className="me-2 mb-2"
                            onClick={() => {
                                if (Object.keys(selectedServices).length === additionalServicesList.length) {
                                    setSelectedServices({})
                                } else {
                                    const newSelected: { [key: string]: boolean } = {}
                                    additionalServicesList.forEach((service: AdditionalServiceType) => {
                                        if (service._id) {
                                            newSelected[service._id] = true
                                        }
                                    })
                                    setSelectedServices(newSelected)
                                }
                            }}
                        >
                            {Object.keys(selectedServices).length === additionalServicesList.length ? 'Deselect All' : 'Select All'}
                        </Button>
                    )}

                    {selectedServices && Object.keys(selectedServices).length > 0 && (
                        <div className="d-flex align-items-center mb-2">
                            <Form.Select
                                size="sm"
                                className="me-2"
                                style={{ width: 'auto' }}
                                value={bulkStatusValue}
                                onChange={(e) => setBulkStatusValue(e.target.value)}
                            >
                                <option value="">Select Status</option>
                                {statusOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.text}</option>
                                ))}
                            </Form.Select>
                            <Button
                                variant="primary"
                                size="sm"
                                className="me-2"
                                disabled={!bulkStatusValue}
                                onClick={() => {
                                    setActiveConfirmationModal({
                                        active: true,
                                        title: 'Update Status',
                                        message: `Are you sure you want to update ${Object.keys(selectedServices).length} service(s) to "${bulkStatusValue}"?`,
                                        onConfirm: updateSelectedStatuses,
                                        onClose: () => {
                                            resetConfirmationModal()
                                        },
                                    })
                                }}
                            >
                                Update Status
                            </Button>
                        </div>
                    )}

                    <div className="additional-services-list-container">
                        {additionalServicesList && additionalServicesList.length > 0 ? (
                            <div>
                                {additionalServicesList.map((additionalService: AdditionalServiceType, idx: number) => {
                                    const isSelected = additionalService._id ? !!selectedServices[additionalService._id] : false
                                    return (
                                        <AdditionalService
                                            key={`${additionalService._id}-${idx}`}
                                            additionalServiceData={additionalService}
                                            customButtons={[
                                                {
                                                    text: isSelected ? 'Deselect' : 'Select',
                                                    variant: isSelected ? 'danger' : 'primary',
                                                    onClick: (serviceData: AdditionalServiceType) => {
                                                        if (!serviceData._id) return
                                                        if (selectedServices[serviceData._id]) {
                                                            const newSelected = { ...selectedServices }
                                                            delete newSelected[serviceData._id]
                                                            setSelectedServices(newSelected)
                                                        } else {
                                                            setSelectedServices({
                                                                ...selectedServices,
                                                                [serviceData._id]: true,
                                                            })
                                                        }
                                                    }
                                                }
                                            ]}
                                        />
                                    )
                                })}
                            </div>
                        ) : (
                            <div className="text-center p-4">
                                <h5>No additional services found</h5>
                                <p>Try adjusting your search filters or add a new service.</p>
                            </div>
                        )}
                    </div>

                    <MongoPagination
                        data={data}
                        loadPage={(pageLoadInfo: { page: number, limit?: number }) => {
                            loadPage({
                                query: activeQuery.query,
                                limit: activeQuery.limit,
                                ...pageLoadInfo
                            })
                        }}
                    />
                </Col>
            </Row>

            <ConfirmModal
                active={activeConfirmationModal.active}
                title={activeConfirmationModal.title}
                message={activeConfirmationModal.message}
                onConfirm={() => {
                    activeConfirmationModal.onConfirm()
                }}
                onClose={() => {
                    activeConfirmationModal.onClose()
                }}
                disabled={isEditing || disableButtons}
            />
        </Container>
    )
}
